// Incremental customer statistics. Pure — no Firebase. Returns the NEXT stats
// object given the previous stats + an order; never recomputes from full history
// (Phase 11 performance), with a recomputeFromOrders() escape hatch for repair.

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const DAY = 24 * 60 * 60 * 1000;

const orderAmount = (order) => num(order?.payment?.total ?? order?.amount ?? order?.price);
const orderProducts = (order) => (order?.items || []).map(i => i.sku || i.name).filter(Boolean);
const orderCourier = (order) => order?.shipping?.courier || order?.courier || '';
const orderMode = (order) => order?.payment?.mode || order?.paymentMode || '';
const orderDate = (order) => order?.createdAt || order?.orderDate || new Date().toISOString();

const topKey = (counts) => Object.entries(counts || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
const bump = (obj, key, by = 1) => { if (!key) return obj; const o = { ...obj }; o[key] = (o[key] || 0) + by; return o; };

// RFM derived from aggregate stats (incremental-friendly; mirrors relationshipCalculator thresholds).
export const rfmFromStats = (stats, now = Date.now()) => {
    const last = stats.lastOrderDate ? new Date(stats.lastOrderDate).getTime() : 0;
    const days = last ? Math.floor((now - last) / DAY) : 9999;
    let r = 1; if (days <= 30) r = 5; else if (days <= 60) r = 4; else if (days <= 90) r = 3; else if (days <= 180) r = 2;
    const o = stats.totalOrders || 0;
    let f = 1; if (o >= 20) f = 5; else if (o >= 10) f = 4; else if (o >= 5) f = 3; else if (o >= 2) f = 2;
    const m = stats.totalRevenue || 0;
    let mo = 1; if (m >= 50000) mo = 5; else if (m >= 20000) mo = 4; else if (m >= 10000) mo = 3; else if (m >= 5000) mo = 2;
    return { r, f, m: mo, score: r * 100 + f * 10 + mo };
};

export const churnRiskFromStats = (stats, now = Date.now()) => {
    const last = stats.lastOrderDate ? new Date(stats.lastOrderDate).getTime() : 0;
    const days = last ? Math.floor((now - last) / DAY) : 9999;
    if (days > 90) return (stats.totalOrders || 0) > 1 ? 'High' : 'Medium';
    if (days > 60) return 'Medium';
    return 'Low';
};

// Health 0-100 (same weighting as customerHealthScore.js).
export const healthFromStats = (stats, now = Date.now()) => {
    const rfm = rfmFromStats(stats, now);
    const churn = churnRiskFromStats(stats, now);
    const complaints = stats.complaintCount || 0;
    const rfmPart = Math.min(45, Math.max(0, ((rfm.score - 111) / 444) * 45));
    const churnPart = churn === 'High' ? 0 : churn === 'Medium' ? 10 : 20;
    const complaintPart = 20 - Math.min(20, complaints * 5);
    return Math.round(Math.min(100, Math.max(0, rfmPart + churnPart + complaintPart + 15)));
};

// Risk 0-100 (higher = riskier): churn + complaint + RTO signals.
export const riskFromStats = (stats, now = Date.now()) => {
    const churn = churnRiskFromStats(stats, now);
    let risk = churn === 'High' ? 50 : churn === 'Medium' ? 25 : 0;
    risk += Math.min(30, (stats.complaintCount || 0) * 10);
    const d = stats.deliveryStats || {};
    if (d.total > 0) risk += Math.min(20, Math.round(((d.rto || 0) / d.total) * 100 / 5));
    return Math.min(100, risk);
};

export const deriveCustomerType = (stats, now = Date.now()) => {
    const last = stats.lastOrderDate ? new Date(stats.lastOrderDate).getTime() : 0;
    const days = last ? Math.floor((now - last) / DAY) : 9999;
    if ((stats.totalOrders || 0) >= 10 || (stats.totalRevenue || 0) >= 20000) return 'VIP';
    if (days > 90 && (stats.totalOrders || 0) >= 1) return 'Dormant';
    if ((stats.totalOrders || 0) > 1) return 'Repeat';
    return 'New';
};

const finalize = (s, now) => {
    const totalOrders = s.totalOrders || 0;
    s.averageOrderValue = totalOrders > 0 ? Math.round((s.totalRevenue || 0) / totalOrders) : 0;
    s.lifetimeValue = s.totalRevenue || 0;
    s.repeatCustomer = totalOrders > 1;
    s.preferredPayment = topKey(s.paymentCounts);
    s.preferredProduct = topKey(s.productCounts);
    s.preferredCourier = topKey(s.courierCounts);
    const d = s.deliveryStats || {};
    s.deliverySuccessPct = d.total > 0 ? Math.round(((d.delivered || 0) / d.total) * 100) : 0;
    s.rfm = rfmFromStats(s, now);
    s.churnRisk = churnRiskFromStats(s, now);
    s.healthScore = healthFromStats(s, now);
    s.riskScore = riskFromStats(s, now);
    s.customerType = deriveCustomerType(s, now);
    return s;
};

// Apply a newly-created order to previous stats (incremental).
export const applyOrderToStats = (prev = {}, order, now = Date.now()) => {
    const s = {
        totalOrders: prev.totalOrders || 0,
        totalRevenue: prev.totalRevenue ?? prev.totalSpent ?? 0,
        firstOrderDate: prev.firstOrderDate || null,
        lastOrderDate: prev.lastOrderDate || null,
        paymentCounts: { ...(prev.paymentCounts || {}) },
        productCounts: { ...(prev.productCounts || {}) },
        courierCounts: { ...(prev.courierCounts || {}) },
        deliveryStats: { ...(prev.deliveryStats || {}) },
        refundStats: { ...(prev.refundStats || {}) },
        complaintCount: prev.complaintCount || 0,
    };
    const date = orderDate(order);
    s.totalOrders += 1;
    s.totalRevenue += orderAmount(order);
    s.lastOrderDate = date;
    if (!s.firstOrderDate || new Date(date) < new Date(s.firstOrderDate)) s.firstOrderDate = date;
    s.paymentCounts = bump(s.paymentCounts, orderMode(order));
    s.courierCounts = bump(s.courierCounts, orderCourier(order));
    orderProducts(order).forEach(p => { s.productCounts = bump(s.productCounts, String(p).replace(/[.#$/[\]]/g, '_')); });
    s.deliveryStats.total = (s.deliveryStats.total || 0) + 1;
    return finalize(s, now);
};

// Apply an order STATUS change. Maintains both the nested stats (backward compat)
// and the flat top-level counters/rates/timestamps requested in Phase 2.
// Supported statuses: delivered | cancelled | returned(rto) | refunded | replacement | reship
export const applyStatusToStats = (prev = {}, status, now = Date.now()) => {
    const s = {
        ...prev,
        totalRevenue: prev.totalRevenue ?? prev.totalSpent ?? 0,
        deliveryStats: { ...(prev.deliveryStats || {}) },
        refundStats: { ...(prev.refundStats || {}) },
        paymentCounts: { ...(prev.paymentCounts || {}) },
        productCounts: { ...(prev.productCounts || {}) },
        courierCounts: { ...(prev.courierCounts || {}) },
        deliveredOrders: prev.deliveredOrders || 0,
        cancelledOrders: prev.cancelledOrders || 0,
        rtoOrders: prev.rtoOrders || 0,
        refundedOrders: prev.refundedOrders || 0,
        replacementOrders: prev.replacementOrders || 0,
        reshipOrders: prev.reshipOrders || 0,
    };
    const d = s.deliveryStats;
    const iso = new Date(now).toISOString();
    switch (status) {
        case 'delivered': d.delivered = (d.delivered || 0) + 1; s.deliveredOrders += 1; s.lastDeliveredAt = iso; break;
        case 'cancelled': d.cancelled = (d.cancelled || 0) + 1; s.cancelledOrders += 1; break;
        case 'returned': d.rto = (d.rto || 0) + 1; d.returned = (d.returned || 0) + 1; s.rtoOrders += 1; s.lastRtoAt = iso; break;
        case 'refunded': s.refundStats.refunds = (s.refundStats.refunds || 0) + 1; s.refundedOrders += 1; s.lastRefundAt = iso; break;
        case 'replacement': s.refundStats.replacements = (s.refundStats.replacements || 0) + 1; s.replacementOrders += 1; break;
        case 'reship': s.reshipOrders += 1; break;
        default: break;
    }
    const denom = s.totalOrders || (s.deliveredOrders + s.cancelledOrders + s.rtoOrders) || 1;
    s.deliverySuccessRate = Math.round((s.deliveredOrders / denom) * 100);
    s.rtoRate = Math.round((s.rtoOrders / denom) * 100);
    s.refundRate = Math.round((s.refundedOrders / denom) * 100);
    return finalize(s, now);
};

// Full recompute from an order list (CSV / repair). Same formulas as incremental.
export const recomputeFromOrders = (orders = [], now = Date.now()) => {
    let s = {};
    [...orders].sort((a, b) => new Date(orderDate(a)) - new Date(orderDate(b)))
        .forEach(o => { s = applyOrderToStats(s, o, now); });
    return s;
};
